import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  IconGolf, IconSearch, IconChevronDown, IconChevronUp,
  IconToggleLeft, IconToggleRight, IconEdit, IconX, IconDeviceFloppy,
  IconDatabaseImport, IconWorldWww, IconSquare, IconSquareCheck, IconPlus, IconTrash
} from '@tabler/icons-react';
import api from '../api/api';

export default function GolfCoursesPage({ me, showToast }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const PAGE_SIZE = 20;

  // 상세/코스 펼치기
  const [expandedId, setExpandedId] = useState(null);
  const [sectionsMap, setSectionsMap] = useState({});

  // 신규 추가 모달
  const [addModal, setAddModal] = useState(false);
  const [addForm, setAddForm] = useState({});
  const [addSaving, setAddSaving] = useState(false);

  // 수정 모달
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [migrating, setMigrating] = useState(false);

  // 신규 골프장 크롤링
  const [crawling, setCrawling] = useState(false);
  const [crawlModal, setCrawlModal] = useState(false);
  const [crawlResult, setCrawlResult] = useState([]);
  const [crawlChecked, setCrawlChecked] = useState({});
  const [crawlAdding, setCrawlAdding] = useState(false);

  // 정렬
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // 코스 섹션 추가/수정
  const [sectionModal, setSectionModal] = useState(null); // { courseId, section(null=신규) }
  const [sectionForm, setSectionForm] = useState({ name: '', holesCount: '', parData: '' });
  const [sectionSaving, setSectionSaving] = useState(false);

  const loadCourses = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/golf/courses', {
        params: { page, size: PAGE_SIZE, keyword: keyword || undefined }
      });
      setCourses(res.data.content);
      setTotalPages(res.data.totalPages);
      setTotalElements(res.data.totalElements);
    } catch {
      showToast('골프장 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, keyword]);

  useEffect(() => { loadCourses(); }, [loadCourses]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedCourses = useMemo(() => {
    if (!sortField) return courses;
    return [...courses].sort((a, b) => {
      const va = (a[sortField] ?? '').toString().toLowerCase();
      const vb = (b[sortField] ?? '').toString().toLowerCase();
      return sortDir === 'asc' ? va.localeCompare(vb, 'ko') : vb.localeCompare(va, 'ko');
    });
  }, [courses, sortField, sortDir]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span style={{ opacity: 0.3, marginLeft: 3 }}>↕</span>;
    return <span style={{ marginLeft: 3 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    setKeyword(searchInput);
  };

  const loadSections = async (courseId) => {
    try {
      const res = await api.get(`/admin/golf/courses/${courseId}/sections`);
      setSectionsMap(prev => ({ ...prev, [courseId]: res.data }));
    } catch {
      showToast('코스 정보를 불러오는데 실패했습니다.', 'error');
    }
  };

  const toggleExpand = (courseId) => {
    if (expandedId === courseId) {
      setExpandedId(null);
    } else {
      setExpandedId(courseId);
      loadSections(courseId);
    }
  };

  const handleToggleUseYn = async (course) => {
    try {
      const res = await api.patch(`/admin/golf/courses/${course.id}/toggle`);
      setCourses(prev => prev.map(c =>
        c.id === course.id ? { ...c, useYn: res.data.useYn } : c
      ));
    } catch {
      showToast('상태 변경에 실패했습니다.', 'error');
    }
  };

  const handleToggleSectionUseYn = async (courseId, section) => {
    try {
      const res = await api.patch(`/admin/golf/sections/${section.id}/toggle`);
      setSectionsMap(prev => ({
        ...prev,
        [courseId]: prev[courseId].map(s =>
          s.id === section.id ? { ...s, useYn: res.data.useYn } : s
        )
      }));
    } catch {
      showToast('코스 상태 변경에 실패했습니다.', 'error');
    }
  };

  const handleMigrate = async () => {
    if (!window.confirm('jp.golf_place + jp.golf_course 데이터를 마이그레이션합니다.\n(골프장 + 코스섹션 + json_data 파싱 포함)\n\n계속하시겠습니까?')) return;
    setMigrating(true);
    try {
      const res = await api.post('/admin/golf/migrate/courses');
      const d = res.data;
      showToast(`마이그레이션 완료 — 골프장: ${d['course.success']}건, 코스: ${d['section.success']}건, 좌표: ${d['json.coordSuccess']}건, 파데이터: ${d['json.section.success']}건`);
      loadCourses();
    } catch {
      showToast('마이그레이션에 실패했습니다.', 'error');
    } finally {
      setMigrating(false);
    }
  };

  const handleCrawlPreview = async () => {
    setCrawling(true);
    try {
      const res = await api.get('/admin/golf/crawl/preview');
      setCrawlResult(res.data.courses || []);
      const checked = {};
      (res.data.courses || []).forEach(c => { checked[c.buddyPlaceKey] = true; });
      setCrawlChecked(checked);
      setCrawlModal(true);
      showToast(`신규 골프장 ${res.data.total}개 발견`);
    } catch {
      showToast('크롤링에 실패했습니다.', 'error');
    } finally {
      setCrawling(false);
    }
  };

  const handleCrawlAdd = async () => {
    const keys = Object.entries(crawlChecked).filter(([, v]) => v).map(([k]) => k);
    if (keys.length === 0) { showToast('추가할 골프장을 선택하세요.', 'error'); return; }
    setCrawlAdding(true);
    try {
      const res = await api.post('/admin/golf/crawl/add', keys);
      showToast(`추가 완료 — 성공: ${res.data.success}, 실패: ${res.data.fail}`);
      setCrawlModal(false);
      loadCourses();
    } catch {
      showToast('추가에 실패했습니다.', 'error');
    } finally {
      setCrawlAdding(false);
    }
  };

  const toggleAllCrawl = (checked) => {
    const next = {};
    crawlResult.forEach(c => { next[c.buddyPlaceKey] = checked; });
    setCrawlChecked(next);
  };

  const openAdd = () => {
    setAddForm({ name: '', tel: '', homepage: '', address: '', intro: '', placeType: '', useYn: 'Y' });
    setAddModal(true);
  };

  const handleAddSave = async () => {
    if (!addForm.name.trim()) { showToast('골프장명을 입력하세요.', 'error'); return; }
    setAddSaving(true);
    try {
      const res = await api.post('/admin/golf/courses', addForm);
      showToast('골프장이 추가되었습니다.');
      setAddModal(false);
      // 새 골프장은 sectionCount=0 이므로 목록 새로고침
      loadCourses();
    } catch {
      showToast('추가에 실패했습니다.', 'error');
    } finally {
      setAddSaving(false);
    }
  };

  const openEdit = (course) => {
    setEditTarget(course);
    setEditForm({
      name: course.name || '',
      tel: course.tel || '',
      homepage: course.homepage || '',
      address: course.address || '',
      intro: course.intro || '',
      useYn: course.useYn || 'Y',
    });
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      const res = await api.put(`/admin/golf/courses/${editTarget.id}`, editForm);
      setCourses(prev => prev.map(c => c.id === editTarget.id ? { ...c, ...res.data } : c));
      showToast('저장되었습니다.');
      setEditTarget(null);
    } catch {
      showToast('저장에 실패했습니다.', 'error');
    } finally {
      setEditSaving(false);
    }
  };

  // 코스 섹션 추가/수정 모달 열기
  const openSectionModal = (courseId, section = null) => {
    setSectionModal({ courseId, section });
    setSectionForm({
      name: section?.name || '',
      holesCount: section?.holesCount || '',
      parData: section?.parData || '',
    });
  };

  const handleSectionSave = async () => {
    if (!sectionModal) return;
    if (!sectionForm.name.trim()) { showToast('코스명을 입력하세요.', 'error'); return; }
    setSectionSaving(true);
    try {
      const payload = {
        name: sectionForm.name.trim(),
        holesCount: sectionForm.holesCount ? Number(sectionForm.holesCount) : null,
        parData: sectionForm.parData.trim() || null,
      };
      let saved;
      if (sectionModal.section) {
        const res = await api.put(`/admin/golf/sections/${sectionModal.section.id}`, payload);
        saved = res.data;
        setSectionsMap(prev => ({
          ...prev,
          [sectionModal.courseId]: prev[sectionModal.courseId].map(s =>
            s.id === sectionModal.section.id ? { ...s, ...saved } : s
          )
        }));
      } else {
        const res = await api.post(`/admin/golf/courses/${sectionModal.courseId}/sections`, payload);
        saved = res.data;
        setSectionsMap(prev => ({
          ...prev,
          [sectionModal.courseId]: [...(prev[sectionModal.courseId] || []), saved]
        }));
        // 섹션 추가됐으므로 목록 sectionCount 업데이트
        setCourses(prev => prev.map(c =>
          c.id === sectionModal.courseId ? { ...c, sectionCount: (c.sectionCount || 0) + 1 } : c
        ));
      }
      showToast('저장되었습니다.');
      setSectionModal(null);
    } catch {
      showToast('저장에 실패했습니다.', 'error');
    } finally {
      setSectionSaving(false);
    }
  };

  const handleDeleteSection = async (courseId, sectionId) => {
    if (!window.confirm('이 코스를 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/admin/golf/sections/${sectionId}`);
      setSectionsMap(prev => ({
        ...prev,
        [courseId]: prev[courseId].filter(s => s.id !== sectionId)
      }));
      setCourses(prev => prev.map(c =>
        c.id === courseId ? { ...c, sectionCount: Math.max(0, (c.sectionCount || 1) - 1) } : c
      ));
      showToast('삭제되었습니다.');
    } catch {
      showToast('삭제에 실패했습니다.', 'error');
    }
  };

  const placeTypeLabel = (type) => {
    const map = { GPT01: '퍼블릭', GPT02: '세미퍼블릭', GPT03: '회원제', GPT04: '기타' };
    return map[type] || type || '-';
  };

  return (
    <div className="container-xl py-3">
      {/* 헤더 */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <IconGolf size={22} />
          <h4 className="mb-0">골프장 관리</h4>
          <span className="badge bg-blue-lt ms-1">{totalElements.toLocaleString()}개</span>
        </div>
        <div className="d-flex gap-2">
          {me?.role === 'MASTER' && (
            <>
              <button className="btn btn-outline-warning btn-sm" onClick={handleMigrate} disabled={migrating}>
                <IconDatabaseImport size={15} className="me-1" />
                {migrating ? '마이그레이션 중...' : '마이그레이션'}
              </button>
              <button className="btn btn-outline-success btn-sm" onClick={handleCrawlPreview} disabled={crawling}>
                <IconWorldWww size={15} className="me-1" />
                {crawling ? '크롤링 중...' : '신규골프장크롤링'}
              </button>
            </>
          )}
          <button className="btn btn-primary btn-sm" onClick={openAdd}>
            <IconPlus size={15} className="me-1" />골프장 추가
          </button>
        <form className="d-flex gap-2" onSubmit={handleSearch}>
          <div className="input-group">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="골프장명, 주소 검색..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              style={{ minWidth: 220 }}
            />
            <button className="btn btn-primary btn-sm" type="submit">
              <IconSearch size={16} />
            </button>
          </div>
        </form>
        </div>
      </div>

      {/* 테이블 */}
      <div className="card">
        <div className="table-responsive">
          <table className="table table-vcenter table-hover card-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>
                  골프장명<SortIcon field="name" />
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('placeType')}>
                  유형<SortIcon field="placeType" />
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('address')}>
                  주소<SortIcon field="address" />
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('tel')}>
                  전화번호<SortIcon field="tel" />
                </th>
                <th style={{ width: 80, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('useYn')}>
                  사용<SortIcon field="useYn" />
                </th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-4 text-muted">
                    <span className="spinner-border spinner-border-sm me-2" />불러오는 중...
                  </td>
                </tr>
              ) : courses.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-4 text-muted">검색 결과가 없습니다.</td>
                </tr>
              ) : sortedCourses.map(course => (
                <React.Fragment key={course.id}>
                  <tr style={{
                    background: expandedId === course.id
                      ? 'var(--tblr-bg-surface-secondary, #f4f6fa)'
                      : (course.sectionCount === 0 && course.useYn === 'Y')
                        ? '#fff0f3'
                        : undefined
                  }}>
                    <td>
                      <button className="btn btn-ghost-secondary btn-icon btn-sm"
                        onClick={() => toggleExpand(course.id)}>
                        {expandedId === course.id
                          ? <IconChevronUp size={14} />
                          : <IconChevronDown size={14} />}
                      </button>
                    </td>
                    <td className="fw-medium">{course.name}</td>
                    <td>
                      <span className="badge bg-azure-lt">{placeTypeLabel(course.placeType)}</span>
                    </td>
                    <td className="text-muted" style={{ fontSize: 13 }}>
                      {course.address || '-'}
                    </td>
                    <td className="text-muted" style={{ fontSize: 13 }}>{course.tel || '-'}</td>
                    <td>
                      <button className="btn btn-ghost-secondary btn-icon btn-sm"
                        onClick={() => handleToggleUseYn(course)}>
                        {course.useYn === 'Y'
                          ? <IconToggleRight size={20} style={{ color: 'var(--tblr-success)' }} />
                          : <IconToggleLeft size={20} style={{ color: 'var(--tblr-muted)' }} />}
                      </button>
                    </td>
                    <td>
                      <button className="btn btn-ghost-secondary btn-icon btn-sm"
                        onClick={() => openEdit(course)}>
                        <IconEdit size={15} />
                      </button>
                    </td>
                  </tr>

                  {/* 코스 펼치기 */}
                  {expandedId === course.id && (
                    <tr>
                      <td colSpan={7} style={{ padding: '0 0 0 48px', background: 'var(--tblr-bg-surface-secondary, #f4f6fa)' }}>
                        <div className="py-2 pe-3">
                          {!sectionsMap[course.id] ? (
                            <span className="text-muted" style={{ fontSize: 13 }}>
                              <span className="spinner-border spinner-border-sm me-1" style={{ width: 12, height: 12 }} />로딩 중...
                            </span>
                          ) : (
                            <>
                              <table className="table table-sm mb-2" style={{ fontSize: 13 }}>
                                <thead>
                                  <tr>
                                    <th>코스명</th>
                                    <th>홀 수</th>
                                    <th>파 데이터</th>
                                    <th>사용</th>
                                    <th style={{ width: 70 }}></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sectionsMap[course.id].length === 0 ? (
                                    <tr>
                                      <td colSpan={5} className="text-muted">코스 정보 없음</td>
                                    </tr>
                                  ) : sectionsMap[course.id].map(s => (
                                    <tr key={s.id}>
                                      <td>{s.name}</td>
                                      <td>{s.holesCount ? `${s.holesCount}홀` : '-'}</td>
                                      <td className="text-muted">{s.parData || '-'}</td>
                                      <td>
                                        <button className="btn btn-ghost-secondary btn-icon btn-sm"
                                          onClick={() => handleToggleSectionUseYn(course.id, s)}>
                                          {s.useYn === 'Y'
                                            ? <IconToggleRight size={18} style={{ color: 'var(--tblr-success)' }} />
                                            : <IconToggleLeft size={18} style={{ color: 'var(--tblr-muted)' }} />}
                                        </button>
                                      </td>
                                      <td>
                                        <button className="btn btn-ghost-secondary btn-icon btn-sm me-1"
                                          onClick={() => openSectionModal(course.id, s)}
                                          title="수정">
                                          <IconEdit size={13} />
                                        </button>
                                        <button className="btn btn-ghost-danger btn-icon btn-sm"
                                          onClick={() => handleDeleteSection(course.id, s.id)}
                                          title="삭제">
                                          <IconTrash size={13} />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              <button className="btn btn-sm btn-outline-primary"
                                onClick={() => openSectionModal(course.id)}>
                                <IconPlus size={13} className="me-1" />코스 추가
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* 페이징 */}
        {totalPages > 1 && (
          <div className="card-footer d-flex align-items-center justify-content-center">
            <ul className="pagination mb-0">
              <li className={`page-item ${page === 0 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(p => p - 1)}>이전</button>
              </li>
              {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                const start = Math.max(0, Math.min(page - 4, totalPages - 10));
                const p = start + i;
                return (
                  <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => setPage(p)}>{p + 1}</button>
                  </li>
                );
              })}
              <li className={`page-item ${page >= totalPages - 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(p => p + 1)}>다음</button>
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* 골프장 추가 모달 */}
      {addModal && (
        <div className="modal modal-blur show d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 520 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">골프장 추가</h5>
                <button className="btn-close" onClick={() => setAddModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">골프장명 <span className="text-danger">*</span></label>
                  <input className="form-control form-control-sm" placeholder="골프장명 입력"
                    value={addForm.name}
                    onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="mb-3">
                  <label className="form-label">유형</label>
                  <select className="form-select form-select-sm" value={addForm.placeType}
                    onChange={e => setAddForm(f => ({ ...f, placeType: e.target.value }))}>
                    <option value="">선택</option>
                    <option value="GPT01">퍼블릭</option>
                    <option value="GPT02">세미퍼블릭</option>
                    <option value="GPT03">회원제</option>
                    <option value="GPT04">기타</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">전화번호</label>
                  <input className="form-control form-control-sm" placeholder="예: 031-123-4567"
                    value={addForm.tel}
                    onChange={e => setAddForm(f => ({ ...f, tel: e.target.value }))} />
                </div>
                <div className="mb-3">
                  <label className="form-label">주소</label>
                  <input className="form-control form-control-sm" placeholder="주소 입력"
                    value={addForm.address}
                    onChange={e => setAddForm(f => ({ ...f, address: e.target.value }))} />
                </div>
                <div className="mb-3">
                  <label className="form-label">홈페이지</label>
                  <input className="form-control form-control-sm" placeholder="https://..."
                    value={addForm.homepage}
                    onChange={e => setAddForm(f => ({ ...f, homepage: e.target.value }))} />
                </div>
                <div className="mb-3">
                  <label className="form-label">소개</label>
                  <textarea className="form-control form-control-sm" rows={3} placeholder="골프장 소개"
                    value={addForm.intro}
                    onChange={e => setAddForm(f => ({ ...f, intro: e.target.value }))} />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-sm" onClick={() => setAddModal(false)}>
                  <IconX size={14} className="me-1" />취소
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleAddSave} disabled={addSaving}>
                  <IconDeviceFloppy size={14} className="me-1" />
                  {addSaving ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 골프장 수정 모달 */}
      {editTarget && (
        <div className="modal modal-blur show d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 520 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">골프장 수정</h5>
                <button className="btn-close" onClick={() => setEditTarget(null)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">골프장명</label>
                  <input className="form-control form-control-sm" value={editForm.name}
                    onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="mb-3">
                  <label className="form-label">전화번호</label>
                  <input className="form-control form-control-sm" value={editForm.tel}
                    onChange={e => setEditForm(f => ({ ...f, tel: e.target.value }))} />
                </div>
                <div className="mb-3">
                  <label className="form-label">홈페이지</label>
                  <input className="form-control form-control-sm" value={editForm.homepage}
                    onChange={e => setEditForm(f => ({ ...f, homepage: e.target.value }))} />
                </div>
                <div className="mb-3">
                  <label className="form-label">주소</label>
                  <input className="form-control form-control-sm" value={editForm.address}
                    onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))} />
                </div>
                <div className="mb-3">
                  <label className="form-label">소개</label>
                  <textarea className="form-control form-control-sm" rows={3} value={editForm.intro}
                    onChange={e => setEditForm(f => ({ ...f, intro: e.target.value }))} />
                </div>
                {editTarget?.latitude && editTarget?.longitude && (
                  <div className="mb-3">
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <label className="form-label mb-0">위치</label>
                      <a
                        href={`https://map.naver.com/p/search/${encodeURIComponent((editTarget.name || '') + (editTarget.address ? ' ' + editTarget.address : ''))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-success"
                        style={{ fontSize: 12 }}
                      >
                        네이버 지도에서 보기 ↗
                      </a>
                    </div>
                    <div style={{ borderRadius: 6, overflow: 'hidden', border: '1px solid var(--tblr-border-color)' }}>
                      <iframe
                        title="map"
                        width="100%"
                        height="220"
                        frameBorder="0"
                        scrolling="no"
                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${editTarget.longitude - 0.01},${editTarget.latitude - 0.01},${editTarget.longitude + 0.01},${editTarget.latitude + 0.01}&layer=mapnik&marker=${editTarget.latitude},${editTarget.longitude}`}
                        style={{ display: 'block' }}
                      />
                    </div>
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label">사용여부</label>
                  <select className="form-select form-select-sm" value={editForm.useYn}
                    onChange={e => setEditForm(f => ({ ...f, useYn: e.target.value }))}>
                    <option value="Y">사용</option>
                    <option value="N">미사용</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-sm" onClick={() => setEditTarget(null)}>
                  <IconX size={14} className="me-1" />취소
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleEditSave} disabled={editSaving}>
                  <IconDeviceFloppy size={14} className="me-1" />
                  {editSaving ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 코스 섹션 추가/수정 모달 */}
      {sectionModal && (
        <div className="modal modal-blur show d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{sectionModal.section ? '코스 수정' : '코스 추가'}</h5>
                <button className="btn-close" onClick={() => setSectionModal(null)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">코스명 <span className="text-danger">*</span></label>
                  <input className="form-control form-control-sm" placeholder="예: East, West, North"
                    value={sectionForm.name}
                    onChange={e => setSectionForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="mb-3">
                  <label className="form-label">홀 수</label>
                  <input className="form-control form-control-sm" type="number" min="1" max="36"
                    placeholder="예: 9, 18"
                    value={sectionForm.holesCount}
                    onChange={e => setSectionForm(f => ({ ...f, holesCount: e.target.value }))} />
                </div>
                <div className="mb-3">
                  <label className="form-label">파 데이터</label>
                  <input className="form-control form-control-sm" placeholder="예: 4,3,5,4,3,4,5,3,4"
                    value={sectionForm.parData}
                    onChange={e => setSectionForm(f => ({ ...f, parData: e.target.value }))} />
                  <div className="form-hint">콤마로 구분된 각 홀의 파 값</div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-sm" onClick={() => setSectionModal(null)}>
                  <IconX size={14} className="me-1" />취소
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleSectionSave} disabled={sectionSaving}>
                  <IconDeviceFloppy size={14} className="me-1" />
                  {sectionSaving ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 신규골프장크롤링 모달 */}
      {crawlModal && (
        <div className="modal modal-blur show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title">신규 골프장 목록</h5>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    골프버디에서 발견된 신규 골프장 <strong>{crawlResult.length}개</strong>
                  </div>
                </div>
                <button className="btn-close" onClick={() => setCrawlModal(false)} />
              </div>
              <div className="modal-body p-0">
                <table className="table table-vcenter table-hover mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <input type="checkbox"
                          checked={crawlResult.length > 0 && crawlResult.every(c => crawlChecked[c.buddyPlaceKey])}
                          onChange={e => toggleAllCrawl(e.target.checked)}
                        />
                      </th>
                      <th>골프장명</th>
                      <th>지역</th>
                      <th style={{ fontSize: 11, color: 'var(--tblr-muted)' }}>Key</th>
                    </tr>
                  </thead>
                  <tbody>
                    {crawlResult.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-4 text-muted">신규 골프장이 없습니다.</td>
                      </tr>
                    ) : crawlResult.map(c => (
                      <tr key={c.buddyPlaceKey}
                        onClick={() => setCrawlChecked(prev => ({ ...prev, [c.buddyPlaceKey]: !prev[c.buddyPlaceKey] }))}
                        style={{ cursor: 'pointer' }}>
                        <td onClick={e => e.stopPropagation()}>
                          <input type="checkbox"
                            checked={!!crawlChecked[c.buddyPlaceKey]}
                            onChange={e => setCrawlChecked(prev => ({ ...prev, [c.buddyPlaceKey]: e.target.checked }))}
                          />
                        </td>
                        <td className="fw-medium">{c.name || '-'}</td>
                        <td className="text-muted" style={{ fontSize: 13 }}>{c.region || '-'}</td>
                        <td className="text-muted" style={{ fontSize: 11 }}>{c.buddyPlaceKey}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                <span className="text-muted me-auto" style={{ fontSize: 13 }}>
                  {Object.values(crawlChecked).filter(Boolean).length}개 선택됨
                </span>
                <button className="btn btn-sm" onClick={() => setCrawlModal(false)}>닫기</button>
                <button className="btn btn-primary btn-sm" onClick={handleCrawlAdd} disabled={crawlAdding}>
                  <IconPlus size={14} className="me-1" />
                  {crawlAdding ? '추가 중...' : '추가'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
